import { useState, useEffect } from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  InputAdornment,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import { colors } from "../source/source.styles";
import { X, Target } from "lucide-react";
import { getEmployeeGoalsApi, updateEmployeeGoalsApi } from "../../../api/reports.api";

const EditGoalsModal = ({ open, onClose, employee }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [goalData, setGoalData] = useState({
    monthlyRevenueGoal: 500000,
    targetConversionRate: 18,
    leadsToBeHandled: 500,
    minimumCallsPerDay: 50,
    targetResponseTime: "< 10 mins",
    efficiencyScoreGoal: 90,
  });

  // Fetch employee goals when modal opens
  useEffect(() => {
    if (open && employee?.employeeId) {
      const fetchGoals = async () => {
        try {
          setLoading(true);
          const response = await getEmployeeGoalsApi(employee.employeeId);
          if (response?.data?.data) {
            const data = response.data.data;
            setGoalData({
              monthlyRevenueGoal: data.monthlyRevenueGoal || 500000,
              targetConversionRate: data.targetConversionRate || 18,
              leadsToBeHandled: data.leadsToBeHandled || 500,
              minimumCallsPerDay: data.minimumCallsPerDay || 50,
              targetResponseTime: data.targetResponseTime || "< 10 mins",
              efficiencyScoreGoal: data.efficiencyScoreGoal || 90,
            });
          }
        } catch (error) {
          console.error("Failed to fetch goals:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchGoals();
    }
  }, [open, employee?.employeeId]);

  const handleFieldChange = (field, value) => {
    setGoalData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateGoals = async () => {
    if (!employee?.employeeId) return;

    try {
      setSaving(true);
      await updateEmployeeGoalsApi(employee.employeeId, {
        period: "quarterly",
        ...goalData,
      });
      onClose();
    } catch (error) {
      console.error("Failed to update goals:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(primaryColor, 0.15),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Target size={20} color={primaryColor} />
            </Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              Edit Performance Goals: {employee?.name || "Employee"}
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{
              color: colors.textMuted,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </Stack>

        {/* Form Content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* SALES TARGETS */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: primaryColor,
                  mb: 2,
                  textTransform: "uppercase",
                }}
              >
                Sales Targets
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Monthly Revenue Goal"
                  type="number"
                  value={goalData.monthlyRevenueGoal}
                  onChange={(e) => handleFieldChange("monthlyRevenueGoal", parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: 14,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: 13,
                      fontWeight: 500,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Target Conversion Rate"
                  type="number"
                  value={goalData.targetConversionRate}
                  onChange={(e) => handleFieldChange("targetConversionRate", parseFloat(e.target.value) || 0)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: 14,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: 13,
                      fontWeight: 500,
                    },
                  }}
                />
              </Stack>
            </Box>

            {/* ACTIVITY TARGETS */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: primaryColor,
                  mb: 2,
                  textTransform: "uppercase",
                }}
              >
                Activity Targets
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Leads to be Handled"
                  type="number"
                  value={goalData.leadsToBeHandled}
                  onChange={(e) => handleFieldChange("leadsToBeHandled", parseFloat(e.target.value) || 0)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: 14,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: 13,
                      fontWeight: 500,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Minimum Calls per Day"
                  type="number"
                  value={goalData.minimumCallsPerDay}
                  onChange={(e) => handleFieldChange("minimumCallsPerDay", parseFloat(e.target.value) || 0)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: 14,
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: 13,
                      fontWeight: 500,
                    },
                  }}
                />
              </Stack>
            </Box>

            {/* QUALITY TARGETS */}
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: primaryColor,
                  mb: 2,
                  textTransform: "uppercase",
                }}
              >
                Quality Targets
              </Typography>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: colors.textSecondary,
                      mb: 0.5,
                    }}
                  >
                    Target Response Time
                  </Typography>
                  <Select
                    fullWidth
                    value={goalData.targetResponseTime}
                    onChange={(e) => handleFieldChange("targetResponseTime", e.target.value)}
                    sx={{
                      borderRadius: 2,
                      fontSize: 14,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.borderLight,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.border,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: primaryColor,
                        borderWidth: 1,
                      },
                    }}
                  >
                    <MenuItem value="< 5 mins">{"< 5 mins"}</MenuItem>
                    <MenuItem value="< 10 mins">{"< 10 mins"}</MenuItem>
                    <MenuItem value="< 15 mins">{"< 15 mins"}</MenuItem>
                    <MenuItem value="< 30 mins">{"< 30 mins"}</MenuItem>
                    <MenuItem value="< 1 hour">{"< 1 hour"}</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: colors.textSecondary,
                      mb: 0.5,
                    }}
                  >
                    Efficiency Score Goal
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={goalData.efficiencyScoreGoal}
                    onChange={(e) => handleFieldChange("efficiencyScoreGoal", parseFloat(e.target.value) || 0)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: primaryColor }}>
                            /100
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontSize: 14,
                      },
                    }}
                  />
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} mt={4}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            fullWidth
            sx={{
              py: 1.25,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              borderColor: colors.border,
              color: colors.textPrimary,
              borderRadius: 2,
              "&:hover": {
                borderColor: colors.border,
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateGoals}
            disabled={saving}
            fullWidth
            sx={{
              py: 1.25,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              bgcolor: primaryColor,
              color: "#FFFFFF",
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                boxShadow: "none",
              },
            }}
          >
            {saving ? "Saving..." : "Update Goals"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default EditGoalsModal;
